import { apiRequest } from './client';
import { clearMenuAccessCache } from './access';
import { encryptData } from '../utils/encryption';

const PROFILE_PHOTO_STORAGE_KEY = 'userProfilePhoto';

export function ensureBypassAuth() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('authToken');
}

export function clearAuthSession() {
  if (typeof window === 'undefined') return;
  // If user is inside an active meeting room, do not wipe session or disrupt the call
  if (window.location.pathname.includes('/meetings/room/')) {
    return;
  }
  clearMenuAccessCache();
  localStorage.removeItem('authToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userRole');
  localStorage.removeItem('employeeId');
  localStorage.removeItem('userName');
  localStorage.removeItem('userEmail');
  localStorage.removeItem(PROFILE_PHOTO_STORAGE_KEY);
  window.dispatchEvent(new Event('hrmsPermissionsUpdated'));
}

export async function refreshAuthToken() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (!token) return null;

  try {
    const response = await apiRequest('/auth/refresh', { silentError: true });
    const payload = response?.data ?? response;
    const newToken = payload?.accessToken || payload?.access_token || payload?.token;
    if (newToken && typeof window !== 'undefined') {
      localStorage.setItem('authToken', newToken);
      return newToken;
    }
  } catch (err) {
    console.warn('Silent session token renewal failed:', err?.message);
  }
  return null;
}

export async function validateAuthSession() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (!token) return false;

  try {
    await apiRequest('/auth/profile');
    return true;
  } catch {
    if (typeof window !== 'undefined' && window.location.pathname.includes('/meetings/room/')) {
      return true; // Keep meeting call alive
    }
    clearAuthSession();
    return false;
  }
}

export async function login({ email, password } = {}) {
  const encryptedPassword = encryptData(password);
  const response = await apiRequest('/auth/login', {
    method: 'POST',
    body: {
      username: email,
      password: encryptedPassword
    }
  });
  const payload = response?.data ?? response;
  const token = payload?.accessToken || payload?.access_token || payload?.token || '';

  if (!token) throw new Error(payload?.message || 'Login failed');

  const user = normalizeUser(payload.user);
  if (typeof window !== 'undefined') {
    clearMenuAccessCache();
    localStorage.setItem('authToken', token);
    localStorage.setItem('userId', String(user.id));
    localStorage.setItem('employeeId', String(user.employeeId || ''));
    localStorage.setItem('userRole', user.role);
    localStorage.setItem('userName', user.fullName);
    localStorage.setItem('userEmail', user.email);
    if (user.profilePhoto) localStorage.setItem(PROFILE_PHOTO_STORAGE_KEY, user.profilePhoto);
    else localStorage.removeItem(PROFILE_PHOTO_STORAGE_KEY);
    window.dispatchEvent(new Event('hrmsPermissionsUpdated'));
  }

  return {
    token,
    accessToken: token,
    user,
    message: payload.message || 'Login Successfully'
  };
}

export async function register({ email, password, fullName, roleId = 3 }) {
  const name = fullName || fullNameFromEmail(email);
  const response = await apiRequest('/user', {
    method: 'POST',
    body: {
      name,
      email,
      password,
      roleId,
      isActive: true
    }
  });

  return {
    success: true,
    user: normalizeUser(response?.data),
    message: response?.message || 'Registration successful'
  };
}

function normalizeUser(user = {}) {
  return {
    id: user.id ?? '',
    employeeId: user.employeeId ?? '',
    email: user.email || '',
    fullName: user.name || user.fullName || fullNameFromEmail(user.email),
    role: user.roleName || user.role?.name || user.role || 'Employee',
    profilePhoto: user.profilePhoto || user.profile_photo || user.profileImage || user.profileImageUrl || user.avatarUrl || ''
  };
}

function fullNameFromEmail(email) {
  if (!email) return 'HRMS User';
  const localPart = email.split('@')[0] || '';
  return localPart.replace(/[._-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}
